import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Divider,
  Chip,
  LinearProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Schedule as TimeIcon,
  Update as UpdateIcon
} from '@mui/icons-material';

const QuickStats = ({ 
  title = "סטטיסטיקות מהירות",
  stats = [],
  loading = false,
  showProgress = false,
  lastUpdated
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const formatValue = (value, type = 'number') => {
    if (loading) return '...';
    if (value === null || value === undefined) return '-';
    
    switch (type) {
      case 'currency':
        return `$${Number(value).toLocaleString('he-IL')}`;
      case 'percentage':
        return `${Number(value).toFixed(1)}%`;
      case 'time':
        return new Date(value).toLocaleTimeString('he-IL');
      case 'date':
        return new Date(value).toLocaleDateString('he-IL');
      default:
        return Number(value).toLocaleString('he-IL');
    }
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) return <TrendingUp color="success" fontSize="small" />;
    if (trend < 0) return <TrendingDown color="error" fontSize="small" />;
    return null;
  };

  const getTrendColor = (trend) => {
    if (trend > 0) return 'success';
    if (trend < 0) return 'error';
    return 'default';
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2
        }}>
          <Typography 
            variant="h6" 
            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            {title}
          </Typography>
          {lastUpdated && (
            <Chip
              icon={<UpdateIcon />}
              label={new Date(lastUpdated).toLocaleTimeString('he-IL')}
              size="small"
              variant="outlined"
              color="primary"
            />
          )}
        </Box>

        {loading && (
          <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
        )}

        {/* Stats Grid */}
        <Grid container spacing={2}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Box sx={{
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'background.default',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'primary.50'
                }
              }}>
                {/* Stat Header */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1
                }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {stat.label}
                  </Typography>
                  {stat.trend !== undefined && getTrendIcon(stat.trend)}
                </Box>

                {/* Main Value */}
                <Typography 
                  variant={isMobile ? 'h6' : 'h5'}
                  sx={{ 
                    fontWeight: 'bold',
                    mb: 0.5,
                    color: stat.color ? `${stat.color}.main` : 'text.primary'
                  }}
                >
                  {formatValue(stat.value, stat.type)}
                </Typography>

                {/* Secondary Info */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  {stat.subtitle && (
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    >
                      {stat.subtitle}
                    </Typography>
                  )}
                  
                  {stat.trend !== undefined && (
                    <Chip
                      size="small"
                      label={`${stat.trend > 0 ? '+' : ''}${stat.trend.toFixed(1)}%`}
                      color={getTrendColor(stat.trend)}
                      variant="outlined"
                      sx={{ 
                        fontSize: '0.7rem',
                        height: 20
                      }}
                    />
                  )}
                </Box>

                {/* Progress Bar */}
                {showProgress && stat.progress !== undefined && (
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={stat.progress}
                      color={stat.color || 'primary'}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: 'grey.200'
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem', mt: 0.5, display: 'block' }}
                    >
                      {stat.progress.toFixed(1)}% מהיעד
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Footer Info */}
        {!loading && stats.length === 0 && (
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 100,
            color: 'text.secondary'
          }}>
            <Typography variant="body2">
              אין נתונים זמינים
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default QuickStats;
